import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DOMPurify from 'dompurify'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { ArrowLeft, Loader2, Lock, Pencil, RotateCcw } from 'lucide-react'
import { EmailBodyEditor } from '@/components/sequences'
import { MissingVariablesNotice } from '@/components/sequences/MissingVariablesNotice'
import {
  useDeleteStepOverride,
  useEnrollmentPreview,
  useSaveStepOverride,
  useSequenceContactPreview,
} from '@/hooks/useApi'
import type { ContactStepOverrides, EnrollOverridesMap, StepContentPreview } from '@/types/api'

export interface PreviewableContact {
  id: string
  full_name?: string | null
  email: string
}

export interface ContactPreviewListProps {
  contacts: PreviewableContact[]
  overridesByContact: EnrollOverridesMap
  onSelect: (contact: PreviewableContact) => void
}

/** Lista cliccabile dei contatti per il drill-down di anteprima (#220). */
export function ContactPreviewList({ contacts, overridesByContact, onSelect }: ContactPreviewListProps) {
  const { t } = useTranslation('common')
  if (contacts.length === 0) return null
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-muted-foreground">{t('contactPreview.listHint')}</p>
      <ul className="max-h-40 overflow-y-auto rounded-md border divide-y">
        {contacts.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left hover:bg-muted/50"
              onClick={() => onSelect(c)}
            >
              <span className="truncate">{c.full_name || c.email}</span>
              {Object.keys(overridesByContact[c.id] ?? {}).length > 0 && (
                <Badge variant="outline" className="text-xs shrink-0 border-amber-400 text-amber-600 dark:text-amber-400">
                  {t('contactPreview.customized')}
                </Badge>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

export interface ContactPreviewPanelProps {
  sequenceId: string
  contact: { id: string; name: string; email: string }
  onBack: () => void
  /** modalità pre-enroll: override pendenti tenuti dal parent */
  draftOverrides?: ContactStepOverrides
  onDraftChange?: (next: ContactStepOverrides) => void
  /** modalità post-enroll: persiste via API */
  enrollmentId?: string
}

export function ContactPreviewPanel({
  sequenceId,
  contact,
  onBack,
  draftOverrides,
  onDraftChange,
  enrollmentId,
}: ContactPreviewPanelProps) {
  const { t } = useTranslation('common')
  const isDraftMode = !enrollmentId
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editSubject, setEditSubject] = useState('')
  const [editBody, setEditBody] = useState('')

  const previewMutation = useSequenceContactPreview()
  const enrollmentPreview = useEnrollmentPreview(enrollmentId)
  const saveOverride = useSaveStepOverride()
  const deleteOverride = useDeleteStepOverride()

  const { mutate: fetchPreview } = previewMutation
  useEffect(() => {
    if (isDraftMode) {
      fetchPreview({ sequence_id: sequenceId, contact_id: contact.id })
    }
  }, [isDraftMode, fetchPreview, sequenceId, contact.id])

  const preview = isDraftMode ? previewMutation.data : enrollmentPreview.data
  const isLoading = isDraftMode ? previewMutation.isPending : enrollmentPreview.isLoading
  const isError = isDraftMode ? previewMutation.isError : enrollmentPreview.isError
  const steps = preview?.steps ?? []
  const step: StepContentPreview | undefined =
    steps.find((s) => s.step_id === selectedStepId) ?? steps[0]

  const draftForStep = (stepId: string) => draftOverrides?.[stepId]
  const stepHasOverride = (s: StepContentPreview) =>
    isDraftMode ? !!draftForStep(s.step_id) : s.has_override

  const draft = step ? draftForStep(step.step_id) : undefined
  // In pre-enroll un draft override si mostra così com'è (i {{token}} restano
  // visibili); post-enroll il server ha già renderizzato l'override.
  const shownSubject = isDraftMode && draft?.subject != null ? draft.subject : step?.subject ?? ''
  const shownBody = isDraftMode && draft?.body_html != null ? draft.body_html : step?.body_html ?? ''

  const startEditing = () => {
    if (!step) return
    const existingSubject = isDraftMode ? draft?.subject : step.override_subject
    const existingBody = isDraftMode ? draft?.body_html : step.override_body_html
    setEditSubject(existingSubject ?? step.subject)
    setEditBody(existingBody ?? step.body_html)
    setEditing(true)
  }

  const handleSave = () => {
    if (!step) return
    if (isDraftMode) {
      onDraftChange?.({
        ...(draftOverrides ?? {}),
        [step.step_id]: { subject: editSubject, body_html: editBody },
      })
    } else if (enrollmentId) {
      saveOverride.mutate({
        enrollment_id: enrollmentId,
        step_id: step.step_id,
        values: { subject: editSubject, body_html: editBody },
      })
    }
    setEditing(false)
  }

  const handleReset = () => {
    if (!step) return
    if (isDraftMode) {
      const next = { ...(draftOverrides ?? {}) }
      delete next[step.step_id]
      onDraftChange?.(next)
    } else if (enrollmentId) {
      deleteOverride.mutate({ enrollment_id: enrollmentId, step_id: step.step_id })
    }
    setEditing(false)
  }

  const selectStep = (stepId: string) => {
    setSelectedStepId(stepId)
    setEditing(false)
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 shrink-0"
          onClick={onBack}
          aria-label={t('back')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <p className="font-medium truncate">
            {t('contactPreview.title', { name: contact.name || contact.email })}
          </p>
          <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('contactPreview.loading')}
        </div>
      )}
      {isError && (
        <p className="text-sm text-destructive py-4">{t('contactPreview.error')}</p>
      )}

      {!isLoading && !isError && step && (
        <>
          {/* Step tabs */}
          <div className="flex gap-1 flex-wrap">
            {steps.map((s) => (
              <Button
                key={s.step_id}
                variant={s.step_id === step.step_id ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => selectStep(s.step_id)}
              >
                {t('contactPreview.step', { order: s.step_order })}
                {stepHasOverride(s) && <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-amber-400" />}
              </Button>
            ))}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {step.locked && (
              <Badge variant="secondary" className="text-xs">
                <Lock className="h-3 w-3 mr-1" />
                {t('contactPreview.locked')}
              </Badge>
            )}
            {stepHasOverride(step) && (
              <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 dark:text-amber-400">
                {t('contactPreview.customized')}
              </Badge>
            )}
          </div>

          <MissingVariablesNotice variables={step.missing_variables} />

          {!editing ? (
            <>
              {/* Read-only preview */}
              <div className="rounded-md border">
                <div className="border-b bg-muted/50 px-3 py-2">
                  <p className="text-xs text-muted-foreground">{t('contactPreview.subject')}</p>
                  <p className="text-sm font-medium">{shownSubject}</p>
                </div>
                <div
                  className="px-3 py-3 text-sm max-h-[40vh] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(shownBody) }}
                />
              </div>
              {!step.locked && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={startEditing}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                    {t('contactPreview.customize')}
                  </Button>
                  {stepHasOverride(step) && (
                    <Button variant="ghost" size="sm" onClick={handleReset}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      {t('contactPreview.reset')}
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Edit mode */}
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('contactPreview.subject')}</label>
                <Input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
                <EmailBodyEditor value={editBody} onChange={setEditBody} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={saveOverride.isPending}>
                  {saveOverride.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  {t('contactPreview.save')}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                  {t('cancel')}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
